import { Drawer, Stack, Typography } from '@mui/material';
import { ResponsiveScatterPlot } from '@nivo/scatterplot';
import React from 'react';
import { NumericFormat } from 'react-number-format';

function CardItemDetails({ open, onClose, item = {}, cardItem = null, recommendation = null, itemUsageLogs = [], cases = [] }) {
    // Calculate linear regression
    const calculateLinearRegression = (data) => {
        if (data.length < 2) return null;
        
        const points = data.map(point => ({
            x: point.x.getTime(),
            y: point.y
        }));
        
        const n = points.length;
        const sumX = points.reduce((sum, point) => sum + point.x, 0);
        const sumY = points.reduce((sum, point) => sum + point.y, 0);
        const sumXY = points.reduce((sum, point) => sum + point.x * point.y, 0);
        const sumXX = points.reduce((sum, point) => sum + point.x * point.x, 0);
        
        const slope = (n * sumXY - sumX * sumY) / (n * sumXX - sumX * sumX);
        const intercept = (sumY - slope * sumX) / n;
        
        return { slope, intercept };
    };

    // Build scatter points (one per case, y=0 if item not present) or fall back to itemUsageLogs
    const scatterPoints = (cases && cases.length > 0)
        ? cases.map(caseData => {
            const used = (caseData.logs || []).filter(l => l.item && l.item.id === item?.id)
                .reduce((s, l) => s + (l.used_quantity || 0), 0);
            return { x: new Date(caseData.date_of_service), y: used };
        })
        : (itemUsageLogs || []).map(log => ({ x: new Date(log.date_of_service), y: log.used_quantity || 0 }));

    const scatterYMax = scatterPoints.length ? Math.max(...scatterPoints.map(p => p.y)) : 0;
    const yScaleMax = scatterYMax === 0 ? 1 : scatterYMax;

    return (
        <Drawer
            anchor="right"
            open={open}
            onClose={onClose}
            slotProps={{ paper : { sx: { width: '30%' } } }}
        >
            <Stack p={3} spacing={2} width={350}>
                <h2>{item?.name}</h2>
                <p>Category: {item?.category}</p>
                <p>Cost: <NumericFormat value={item?.cost / 100} displayType={'text'} thousandSeparator={true} prefix={'$'} /></p>
            </Stack>
            {((cases && cases.length > 0) || (itemUsageLogs && itemUsageLogs.length > 0)) && (
                <div style={{ height: 250, width: '100%' }}>
                    <ResponsiveScatterPlot
                        data={[{ id: 'usage', data: scatterPoints }]}
                        margin={{ top: 20, right: 20, bottom: 60, left: 60 }}
                        yScale={{ type: 'linear', min: 0, max: yScaleMax }}
                        xScale={{ type: 'time' }}
                        axisBottom={{
                            format: '%m/%d',
                            tickValues: 'every 1 month',
                            legend: 'Date of Service',
                            legendOffset: 45,
                            legendPosition: 'middle'
                        }}
                        axisLeft={{
                            legend: 'Used Quantity',
                            legendOffset: -45,
                            legendPosition: 'middle'
                        }}
                        colors={{ scheme: 'category10' }}
                        pointSize={8}
                        pointBorderWidth={1}
                        pointBorderColor={{ from: 'serieColor' }}
                        useMesh={true}
                        animate={true}
                        motionStiffness={120}
                        motionDamping={50}
                        tooltip={() => null}
                        layers={[
                            'grid',
                            'axes',
                            'nodes',
                            'mesh',
                            'legends',
                            // Custom layer for regression line
                            ({ nodes, xScale, yScale }) => {
                                // `nodes` is an array of rendered point objects; use their original data
                                const points = (nodes || []).map(n => ({
                                    x: n.data.x instanceof Date ? n.data.x : new Date(n.data.x),
                                    y: typeof n.data.y === 'number' ? n.data.y : Number(n.data.y) || 0,
                                }));
                                if (points.length < 2) return null;

                                const regression = calculateLinearRegression(points);
                                if (!regression) return null;

                                const { slope, intercept } = regression;
                                const xMin = Math.min(...points.map(p => p.x.getTime()));
                                const xMax = Math.max(...points.map(p => p.x.getTime()));

                                const yStart = slope * xMin + intercept;
                                const yEnd = slope * xMax + intercept;

                                return (
                                    <line
                                        x1={xScale(new Date(xMin))}
                                        y1={yScale(yStart)}
                                        x2={xScale(new Date(xMax))}
                                        y2={yScale(yEnd)}
                                        stroke="#ff6b6b"
                                        strokeWidth={2}
                                        strokeDasharray="5,5"
                                    />
                                );
                            }
                        ]}
                    />
                </div>
            )}
            <Stack spacing={2} p={3}>
                <Typography variant='bold'>Current Card Quantities:</Typography>
                { cardItem ? (
                    <>
                        <Typography>Open Quantity: {cardItem.open_quantity || 0}</Typography>
                        <Typography>Hold Quantity: {cardItem.hold_quantity || 0}</Typography>
                    </>
                ) : (
                    <Typography>Item is not currently on the card.</Typography>
                )}
                { recommendation && (
                    <Typography variant='bold' mt={2}>Associated Recommendation:</Typography>
                )}
                { recommendation ? (
                    <>
                        <Typography>Type: {recommendation.type}</Typography>
                        <Typography>Value: <NumericFormat value={recommendation.recommendation_value / 100} displayType={'text'} thousandSeparator={true} prefix={'$'} /></Typography>
                        <Typography>Status: {recommendation.status}</Typography>
                    </>
                ) : (
                    <Typography>No current recommendations for this item.</Typography>
                )}
            </Stack>
        </Drawer>
    );
}

export default CardItemDetails;
